import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../../firebase';

const FUNCTIONS_BASE_URL = 'https://us-central1-meu-controle-de-estoque-2.cloudfunctions.net';

const AdminView = () => {
  const [users, setUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actioningId, setActioningId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const user = auth.currentUser;
    if (!user) {
      setError("Acesso negado. Por favor, faça login como administrador.");
      setLoading(false);
      return;
    }

    try {
      const token = await user.getIdToken();
      const [usersResponse, blockedUsersResponse] = await Promise.all([
        fetch(`${FUNCTIONS_BASE_URL}/listUsers`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(`${FUNCTIONS_BASE_URL}/listBlockedUsers`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
      ]);

      if (!usersResponse.ok) {
        const errorData = await usersResponse.json();
        throw new Error(errorData.error || `Erro ao buscar usuários`);
      }
       if (!blockedUsersResponse.ok) {
        const errorData = await blockedUsersResponse.json();
        throw new Error(errorData.error || `Erro ao buscar usuários bloqueados`);
      }

      const usersResult = await usersResponse.json();
      const blockedUsersResult = await blockedUsersResponse.json();
      
      const sortedUsers = usersResult.users.sort((a, b) => {
        if (a.status === 'pendente' && b.status !== 'pendente') return -1;
        if (a.status !== 'pendente' && b.status === 'pendente') return 1;
        return (b.createdAt?._seconds || 0) - (a.createdAt?._seconds || 0);
      });

      setUsers(sortedUsers);
      setBlockedUsers(blockedUsersResult.blockedUsers);

    } catch (err) {
      console.error("Erro ao carregar dados do admin:", err);
      setError(err.message || "Falha ao obter os dados.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // A função handleAction já está pronta para a ação 'reject'
  const handleAction = async (action, userPayload) => {
    setActioningId(userPayload.uid);
    let functionName;
    let payload = { uid: userPayload.uid };

    switch (action) {
        case 'approve':
            functionName = 'approveUser';
            payload.makeAdmin = false;
            break;
        case 'approveAsAdmin':
            functionName = 'approveUser';
            payload.makeAdmin = true;
            break;
        case 'reject': // Esta ação será usada tanto para rejeitar quanto para bloquear
            functionName = 'rejectUser';
            break;
        case 'makeAdmin':
            functionName = 'addAdminRole';
            payload = { email: userPayload.email };
            break;
        case 'removeAdmin':
            functionName = 'removeAdminRole';
            break;
        case 'delete':
            functionName = 'deleteUser';
            break;
        default:
            setActioningId(null);
            return;
    }

    // O resto da função handleAction permanece o mesmo...
    const user = auth.currentUser;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${FUNCTIONS_BASE_URL}/${functionName}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'A ação falhou no servidor.');
      }
      fetchData();
    } catch (err) {
      setError(`Erro ao executar a ação '${action}': ${err.message}`);
      fetchData();
    } finally {
      setActioningId(null);
    }
  };

  const handleUnblock = async (email) => {
    setActioningId(email);
    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();
      const response = await fetch(`${FUNCTIONS_BASE_URL}/unblockUser`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
       if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'A ação de desbloquear falhou no servidor.');
      }
      fetchData();
    } catch (err) {
       setError(`Erro ao desbloquear o e-mail ${email}: ${err.message}`);
       fetchData();
    } finally {
       setActioningId(null);
    }
  };

  if (loading) {
    return <div className="text-center p-10">A carregar dados de administração...</div>;
  }

  if (error) {
    return <div className="text-center p-10 text-red-600">Ocorreu um erro: {error}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      {/* Tabela de Gestão de Usuários */}
      <h1 className="text-2xl font-bold mb-4">Painel de Administração</h1>
      <p className="mb-6 text-gray-600">Gira o acesso e as permissões dos usuários da sua aplicação.</p>
      <div className="bg-white shadow-md rounded-lg overflow-x-auto mb-10">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email / Status</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((u) => (
              <tr key={u.uid} className={actioningId === u.uid ? 'bg-gray-100 opacity-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{u.email}</div>
                    <div className="text-sm text-gray-500 flex items-center mt-1">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            u.status === 'pendente' ? 'bg-yellow-100 text-yellow-800' :
                            u.status === 'ativo' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                        }`}>
                          {u.status}
                        </span>
                        {u.isAdmin && (
                            <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                Admin
                            </span>
                        )}
                    </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-4">
                    {u.status === 'pendente' && (
                      <>
                        <button onClick={() => handleAction('approve', u)} disabled={!!actioningId} className="text-green-600 hover:text-green-900 disabled:opacity-50">Aprovar</button>
                        <button onClick={() => handleAction('approveAsAdmin', u)} disabled={!!actioningId} className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50">Aprovar como Admin</button>
                        <button onClick={() => handleAction('reject', u)} disabled={!!actioningId} className="text-red-600 hover:text-red-900 disabled:opacity-50">Rejeitar</button>
                      </>
                    )}
                    {u.status === 'ativo' && u.uid !== auth.currentUser.uid && (
                      <>
                        {u.isAdmin ? (
                            <button onClick={() => handleAction('removeAdmin', u)} disabled={!!actioningId} className="text-yellow-600 hover:text-yellow-900 disabled:opacity-50">Remover Admin</button>
                        ) : (
                            <button onClick={() => handleAction('makeAdmin', u)} disabled={!!actioningId} className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50">Tornar Admin</button>
                        )}
                        {/* --- BOTÃO ATUALIZADO/ADICIONADO --- */}
                        {/* O antigo botão "Excluir Usuário" foi substituído por este, que é mais completo */}
                        <button onClick={() => handleAction('reject', u)} disabled={!!actioningId} className="text-red-600 hover:text-red-900 disabled:opacity-50">Bloquear e Excluir</button>
                      </>
                    )}
                    {u.uid === auth.currentUser.uid && (
                        <span className="text-xs text-gray-400"> (Você) </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tabela de Usuários Bloqueados (sem alteração) */}
      <h2 className="text-2xl font-bold mb-4">Usuários Bloqueados (Blocklist)</h2>
      <p className="mb-6 text-gray-600">Remova e-mails da lista para permitir que eles se registem novamente.</p>
       <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email Bloqueado</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {blockedUsers.length > 0 ? blockedUsers.map((bu) => (
              <tr key={bu.email} className={actioningId === bu.email ? 'bg-gray-100 opacity-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{bu.email}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button 
                    onClick={() => handleUnblock(bu.email)}
                    disabled={!!actioningId}
                    className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                  >
                    Desbloquear
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="2" className="px-6 py-4 text-center text-gray-500">Nenhum usuário bloqueado.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminView;